import { useRef, useCallback, useEffect, useState } from 'react';

interface UseAudioOptions {
  src: string;
  volume?: number;
  loop?: boolean;
  autoplay?: boolean;
  onEnded?: () => void;
  onPlay?: () => void;
  onPause?: () => void;
  onError?: (error: Error) => void;
}

export function useAudio({
  src,
  volume = 1,
  loop = false,
  autoplay = false,
  onEnded,
  onPlay,
  onPause,
  onError,
}: UseAudioOptions) {
  const audioRef = useRef<HTMLAudioElement>();
  const playPromiseRef = useRef<Promise<void>>();
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [currentSrc, setCurrentSrc] = useState(src);

  useLayoutEffect(() => {
    audioRef.current! = new Audio(src);
  }, [src]);

  useEffect(() => {
    const audio = audioRef.current!;
    audio.volume = volume;
    audio.loop = loop;

    if (autoplay) {
      audio.play().catch((error) => {
        onError?.(error);
      });
    }

    const handleEnded = () => {
      setIsPlaying(false);
      onEnded?.();
    };

    const handlePlay = () => {
      setIsPlaying(true);
      onPlay?.();
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPause?.();
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
    };

    const handleError = (e: ErrorEvent) => {
      onError?.(new Error(e.message));
    };

    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('error', handleError as EventListener);

    return () => {
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('error', handleError as EventListener);
      audio.pause();
    };
  }, [src, volume, loop, autoplay, onEnded, onPlay, onPause, onError]);

  const updateSrc = useCallback(
    (newSrc: string) => {
      if (newSrc !== currentSrc) {
        const wasPlaying = isPlaying;
        const currentVolume = audioRef.current!.volume;
        const wasMuted = isMuted;

        audioRef.current!.pause();
        audioRef.current!.currentTime = 0;

        const newAudio = new Audio(newSrc);
        newAudio.volume = currentVolume;
        newAudio.muted = wasMuted;
        newAudio.loop = loop;

        audioRef.current! = newAudio;
        setCurrentSrc(newSrc);
        setCurrentTime(0);
        setDuration(0);

        if (wasPlaying) {
          newAudio.play().catch((error) => {
            onError?.(error);
          });
        }
      }
    },
    [currentSrc, isPlaying, isMuted, loop, onError]
  );

  const play = useCallback(async () => {
    try {
      if (playPromiseRef.current) {
        await playPromiseRef.current;
      }

      audioRef.current!.currentTime = 0;
      playPromiseRef.current = audioRef.current!.play();
      await playPromiseRef.current;
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error('audio play error', error);
        onError?.(error as Error);
      }
    } finally {
      playPromiseRef.current = undefined;
    }
  }, [onError]);

  const resume = useCallback(async () => {
    try {
      if (playPromiseRef.current) {
        await playPromiseRef.current;
      }

      playPromiseRef.current = audioRef.current!.play();
      await playPromiseRef.current;
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        onError?.(error as Error);
      }
    } finally {
      playPromiseRef.current = undefined;
    }
  }, [onError]);

  const pause = useCallback(() => {
    if (playPromiseRef.current) {
      playPromiseRef.current
        .then(() => {
          audioRef.current!.pause();
        })
        .catch(() => {
          // 忽略中断错误
        });
    } else {
      audioRef.current!.pause();
    }
  }, []);

  const stop = useCallback(() => {
    audioRef.current!.pause();
    audioRef.current!.currentTime = 0;
  }, []);

  const seek = useCallback(
    (time: number) => {
      if (time >= 0 && time <= duration) {
        audioRef.current!.currentTime = time;
      }
    },
    [duration]
  );

  const setVolume = useCallback((newVolume: number) => {
    if (newVolume >= 0 && newVolume <= 1) {
      audioRef.current!.volume = newVolume;
    }
  }, []);

  const toggleMute = useCallback(() => {
    audioRef.current!.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  return {
    play,
    resume,
    pause,
    stop,
    seek,
    setVolume,
    toggleMute,
    updateSrc,
    audioRef,
    isPlaying,
    duration,
    currentTime,
    isMuted,
    volume: audioRef.current?.volume,
    currentSrc,
  };
}
